/**
 * @file 请求配置
 * @author hardylin <hardylin@tencent.com>
 */
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { jsonp as sfJsonp, stringify } from '@tencent/slug-function'

import { getUrlParam } from './utils/index'
import store from './store'

const source = 'ingame'
const tgl_id = store.getters['global/tglId'];

/**
 * 微社区接口
 * @param {Object} options - jsonp 请求 `options` 参数
 */
export function requestJsonp(options) {
    let { url, data = {}, noToken = false } = options
    let path = '//app.ingame.qq.com/php/ingame'
    let tokens = sessionStorage.tokenParams || ''

    // 测试环境
    if (sessionStorage.igEnv == 'development') {
        path = '//appt.ingame.qq.com/php/ingame'
        data.sandbox = 1
    }

    return sfJsonp({
        url: `${path}${url}${noToken ? '?' : tokens + ''}`,
        data: Object.assign({
            game: store.state.global.game,
            gameid: store.state.global.game,
            bid: source,
            channel: 1
        }, data)
    })
}

/**
 * 微社区接口
 * @param {Object} data - axios 请求参数
 * @param {Object} data.url - axios 请求 `url` 参数
 * @param {Object} data.params - axios 请求 `params` 参数
 */
export function request(data) {
    const { url, params = {} } = data

    // 测试环境
    if (sessionStorage.igEnv == 'development') {
        params.sandbox = 1
    }

    return axios({
        baseURL: `https://${sessionStorage.igEnv == 'development' ? 'appt' : 'app'}.ingame.qq.com`,
        url: `${url}${sessionStorage.tokenParams}`,
        params: Object.assign({
            game: store.state.global.game,
            gameid: store.state.global.game,
            bid: source,
            channel: 1
        }, params)
    })
}

/**
 * 微社区 jsonp 接口
 * @param {Object} options - jsonp 请求 `options` 参数
 */
export function jsonp(options) {
    let { url, data = {}, noToken = false } = options
    let path = '//app.ingame.qq.com/php/ingame'

    // 测试环境
    if (sessionStorage.igEnv == 'development') {
        path = '//appt.ingame.qq.com/php/ingame'
        data.sandbox = 1
    }

    return sfJsonp({
        url: `${path}${url}${noToken || !sessionStorage.tokenParams ? '?' : sessionStorage.tokenParams}`,
        data: Object.assign({
            game: store.state.global.game,
            gameid: store.state.global.game,
            bid: source,
            channel: 1
        }, data)
    }).then(result => {
        if (result.status.toLowerCase() == 'success') {
            return result.data
        } else {
            Sentry.addBreadcrumb({
                category: store.state.global.game,
                message: url,
                level: 'error'
            })
            throw result
        }
    })
}

/**
 * 微社区 GET 接口
 * @param {Object} config - axios 请求参数
 * @param {Object} config.url - axios 请求 `url` 参数
 * @param {Object} config.params - axios 请求 `params` 参数
 * @param {Boolean} [config.carryToken=true] - 是否携带登录态
 */
export function get(config) {
    let { url, params = {}, carryToken = true } = config

    // 测试环境
    if (sessionStorage.igEnv == 'development') {
        params.sandbox = 1
    }

    return axios({
        baseURL: `//${sessionStorage.igEnv == 'development' ? 'appt' : 'app'}.ingame.qq.com`,
        url: `${url}${carryToken ? (sessionStorage.tokenParams || '') : ''}`,
        method: 'GET',
        params: Object.assign({
            game: store.state.global.game,
            gameid: store.state.global.game,
            bid: source,
            channel: 1
        }, params)
    }).then(result => {
        if (result.data && (result.data.code == 0 || result.data.status && result.data.status.toLowerCase() == 'success')) {
            return result.data.data
        } else {
            Sentry.addBreadcrumb({
                category: store.state.global.game,
                message: url,
                level: 'error'
            })
            throw result
        }
    })
}

/**
 * 微社区 POST 接口
 * @param {Object} config - axios 请求参数
 * @param {String} config.url - axios 请求 `url` 参数
 * @param {Object} config.data - axios 请求 `data` 参数
 * @returns {Object} - Promise 对象
 */
export function postIngame(config) {
    let { baseUrl, url, data = {}, query = {} } = config

    /* #region 参数处理 */
    data = Object.assign({
        game: store.state.global.game,
        bid: source,
        channel: 1
    }, data)
    let formData = new FormData()
    Object.entries(data).forEach(item => {
        formData.append(item[0], item[1])
    })
    /* #endregion */

    return axios({
        baseURL: baseUrl ? baseUrl : `//${sessionStorage.igEnv == 'development' ? 'appt' : 'app'}.ingame.qq.com`,
        url: `${url}${sessionStorage.tokenParams ? sessionStorage.tokenParams : '?'}${sessionStorage.igEnv == 'development' ? '&sandbox=1' : ''}`,
        method: 'POST',
        params: query,
        data: formData,
    }).then(result => {
        if (result.data && result.data.status.toLowerCase() == 'success') {
            return result.data.data
        } else {
            Sentry.addBreadcrumb({
                category: store.state.global.game,
                message: url,
                level: 'error'
            })
            throw result
        }
    })
}

/**
 * TGL POST 接口
 * @param {Object} config - axios 请求参数
 * @param {Object} config.url - axios 请求 `url` 参数
 * @param {Object} [config.data] - axios 请求 `data` 参数
 * @param {Object} [config.params] - axios 请求 `params` 参数
 */
export function postTGL(config) {
    let { url, data = {} } = config
    if (data instanceof FormData) {
        data.append('gameid', tgl_id)
        data.append('openid', getUrlParam('openid', sessionStorage.tokenParams))
    } else {
        data = Object.assign({
            gameid: tgl_id, // 一起来捉妖
            openid: getUrlParam('openid', sessionStorage.tokenParams),
        }, data)
    }
    return axios({
        baseURL: `//${sessionStorage.igEnv == 'development' ? 'pre.' : ''}doujin.qq.com`,
        url: `${url}`,
        method: 'POST',
        data,
    })
}

/**
 * V4 接口
 * @param {Object} data - axios 请求参数
 * @param {Object} data.url - axios 请求 `url` 参数
 * @param {Object} data.params - axios 请求 `params` 参数
 */
export function requestV4(data) {
    const { config } = window
    const { url, params } = data
    return axios({
        baseURL: 'https://apps.game.qq.com',
        url,
        params: Object.assign({
            p0: config.p0,
            source: 'ingame',
        }, params)
    })
}

/**
 * V4 标签交叉搜索接口
 * @param {Object} params - axios 请求 `params` 参数
 * @param {Number} params.limit - 一次请求条数
 * @param {String} [params.logic='or'] - 标签间关系：and（交集），or（并集）
 * @param {Number} params.start - 从第几条开始请求
 * @param {String} [params.sortby='sIdxTime'] - 排序：iTotalPlay（阅读量），sIdxTime（发布时间）
 * @param {String} params.tagids  - 标签 id，多个标签使用 `,` 分隔，最多三个
 */
export function requestV4MixedList(params) {
    const { config } = window
    return axios({
        baseURL: 'https://apps.game.qq.com',
        url: '/cmc/cross',
        params: Object.assign({
            filter: 'tag',
            logic: 'or',
            serviceId: config.p0,
            sortby: 'sIdxTime',
            typeids: '1,2',
            source,
        }, params)
    })
}

/**
 * Axios POST 请求
 * @param {Object} config - axios 请求参数
 */
function post(config) {
    let {
        url,
        data = {}
    } = config
    return axios({
        method: 'POST',
        url,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: stringify(data)
    })
}

export {
    post
}
